import dayjs from "dayjs";

// Function to process data and create grid columns and rows
export const processReportData = (extensionRequestList, appliedFilters = null, filters, newReasonCodeOptions, selectedCustomerName = '') => {
  if (!extensionRequestList || extensionRequestList.length === 0) {
    return { processedColumns: [], processedRows: [] };
  }

  // Filter by customer name first if selected
  let filteredData = extensionRequestList;
  if (selectedCustomerName && selectedCustomerName.trim() !== '') {
    filteredData = extensionRequestList.filter(item => 
      item.customerName && item.customerName === selectedCustomerName
    );
  }

  // Filter by new reason code category if applied filters exist and new reason code is selected
  if (appliedFilters && appliedFilters.newReasonCode && appliedFilters.newReasonCode.trim() !== '') {
    filteredData = filteredData.filter(item => {
      if (!item.newReasonCode) return false;
      
      // Categorize the reason code
      let categoryKey = '';
      if ((item.newReasonCode.startsWith('F') && !item.newReasonCode.startsWith('FU')) || item.newReasonCode.startsWith('FS')) {
        categoryKey = 'F/FS';
      } else if (item.newReasonCode.startsWith('CF') || item.newReasonCode.startsWith('CL')) {
        categoryKey = 'CF/CL';
      } else if (item.newReasonCode.startsWith('FU')) {
        categoryKey = 'FU';
      }
      
      return categoryKey === appliedFilters.newReasonCode;
    });
  }

  // Generate months based on user's selected range in filter drawer or applied filters or default to last 6 months
  let monthsToShow = [];
  
  // Priority: 1. Current filter selections (if complete), 2. Applied filters, 3. Default
  const useFilters = (filters && filters.startMonth && filters.endMonth && filters.startYear && filters.endYear) 
    ? filters 
    : (appliedFilters && appliedFilters.startMonth && appliedFilters.endMonth && appliedFilters.startYear && appliedFilters.endYear) 
      ? appliedFilters 
      : null;
  
  if (useFilters) {
    // Generate ALL months from start to end based on selected date range - no limit
    const startDate = dayjs(`${useFilters.startYear}-${String(useFilters.startMonth).padStart(2, '0')}-01`);
    const endDate = dayjs(`${useFilters.endYear}-${String(useFilters.endMonth).padStart(2, '0')}-01`);
        
    let currentDate = startDate;
    // Use comparison with unix timestamps to avoid plugin dependency
    while (currentDate.valueOf() <= endDate.valueOf()) {
      monthsToShow.push({
        key: currentDate.format('YYYY-MM'),
        label: currentDate.format('MMM-YY')
      });
      currentDate = currentDate.add(1, 'month');
    }
    
  } else {
    // Default: show last 6 months if no date range is selected
    const currentDate = dayjs();
    for (let i = 5; i >= 0; i--) {
      const monthDate = currentDate.subtract(i, 'month');
      monthsToShow.push({
        key: monthDate.format('YYYY-MM'),
        label: monthDate.format('MMM-YY')
      });
    }
  }

  // Process data by new reason code category and month
  const dataMap = {};

  // Get the set of valid months to show for filtering
  const validMonthKeys = new Set(monthsToShow.map(month => month.key));

  // Count items and sum values by new reason code category and month based on processing time
  filteredData.forEach((item, index) => {
    if (item.newReasonCode && item.processingtime) {
      const processingMonth = dayjs(item.processingtime).format('YYYY-MM');
      
      // Only process items that fall within the selected/default month range
      if (!validMonthKeys.has(processingMonth)) {
        return; // Skip this item if it's outside the valid month range
      }
      
      // Categorize the reason code
      let reasonCodeCategory = '';
      if ((item.newReasonCode.startsWith('F') && !item.newReasonCode.startsWith('FU')) || item.newReasonCode.startsWith('FS')) {
        reasonCodeCategory = 'F/FS';
      } else if (item.newReasonCode.startsWith('CF') || item.newReasonCode.startsWith('CL')) {
        reasonCodeCategory = 'CF/CL';
      } else if (item.newReasonCode.startsWith('FU')) {
        reasonCodeCategory = 'FU';
      } else {
        reasonCodeCategory = 'Others'; // For any other codes
      }

      // Get the individual reason code for detailed display
      let detailReasonCode = item.newReasonCode;
      
      // For F/FS category, show individual codes up to 6, then group as "Others"
      if (reasonCodeCategory === 'F/FS') {
        if (detailReasonCode.startsWith('F') && !detailReasonCode.startsWith('FU') && !detailReasonCode.startsWith('FS')) {
          // Handle F codes (F1, F2, F3, F4, F5, F6)
          const numPart = detailReasonCode.substring(1); // Remove 'F'
          const num = parseInt(numPart);
          if (!isNaN(num) && num > 6) {
            detailReasonCode = 'F-Others';
          }
          // Keep F1-F6 as is
        } else if (detailReasonCode.startsWith('FS')) {
          // Handle FS codes (FS1, FS2, FS3, FS4, FS5, FS6)
          const numPart = detailReasonCode.substring(2); // Remove 'FS'
          const num = parseInt(numPart);
          if (!isNaN(num) && num > 6) {
            detailReasonCode = 'FS-Others';
          }
          // Keep FS1-FS6 as is
        } else {
          // Any other F-type codes that don't match pattern
          detailReasonCode = 'F-Others';
        }
      } else {
        // For other categories, apply the original logic
        if (detailReasonCode) {
          let shouldCategorizeAsOthers = false;
          
          // Check CF codes (CF7, CF8, CF9, etc.)
          if (detailReasonCode.startsWith('CF')) {
            const numPart = detailReasonCode.substring(2); // Remove 'CF'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          // Check CL codes (CL7, CL8, CL9, etc.)
          else if (detailReasonCode.startsWith('CL')) {
            const numPart = detailReasonCode.substring(2); // Remove 'CL'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          // Check FU codes (FU7, FU8, FU9, etc.)
          else if (detailReasonCode.startsWith('FU')) {
            const numPart = detailReasonCode.substring(2); // Remove 'FU'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          
          if (shouldCategorizeAsOthers) {
            detailReasonCode = 'Others';
          }
        }
      }
      
      const compositeKey = `${reasonCodeCategory}|${detailReasonCode}`;
      
      // Only process records that fall within the selected month range
      const isMonthInRange = monthsToShow.some(month => month.key === processingMonth);
      
      if (isMonthInRange) {
        // Ensure the composite key exists in dataMap
        if (!dataMap[compositeKey]) {
          dataMap[compositeKey] = {
            reasonCode: detailReasonCode,
            monthlyData: {}
          };
          // Initialize all months in monthsToShow
          monthsToShow.forEach(month => {
            dataMap[compositeKey].monthlyData[month.key] = { count: 0, value: 0 };
          });
        }
        
        // Initialize the month if it doesn't exist
        if (!dataMap[compositeKey].monthlyData[processingMonth]) {
          dataMap[compositeKey].monthlyData[processingMonth] = { count: 0, value: 0 };
        }
        
        dataMap[compositeKey].monthlyData[processingMonth].count++;
        // Add the amount to extend for value calculation
        const amountToExtend = parseFloat(item.amountToExtend) || 0;
        dataMap[compositeKey].monthlyData[processingMonth].value += amountToExtend;
      }
    }
  });

  // Determine column header suffix based on current volume type (immediate effect)
  const volumeType = filters.volume;

  // Create columns - Reason Code and months
  const processedColumns = [
    {
      field: "reasonCode",
      headerName: "Reason Code",
      width: 160,
      align: "left",
      headerAlign: "left",
      filterable: true,
    },
    ...monthsToShow.map(month => ({
      field: month.key,
      headerName: month.label,
      width: 120,
      align: "center",
      headerAlign: "center",
      type: 'number',
      filterable: true
    }))
  ];

  // Create rows - show only reason code categories and codes that exist in the actual data
  const allCompositeKeys = Object.keys(dataMap);
  
  // Custom sorting function for reason code categories: F/FS → CF/CL → FU → others
  const sortByReasonCodeCategoryAndCode = (keys) => {
    const priorityOrder = ['F/FS', 'CF/CL', 'FU'];
    
    return keys.sort((a, b) => {
      const [categoryA, codeA] = a.split('|');
      const [categoryB, codeB] = b.split('|');
      
      const indexA = priorityOrder.indexOf(categoryA);
      const indexB = priorityOrder.indexOf(categoryB);
      
      // First sort by reason code category priority
      if (indexA !== -1 && indexB !== -1) {
        if (indexA !== indexB) {
          return indexA - indexB;
        }
        
        // Within F/FS category, apply special sorting: F1-F6, FS1-FS6, then F-Others, FS-Others
        if (categoryA === 'F/FS' && categoryB === 'F/FS') {
          return sortFFSCodes(codeA, codeB);
        }
        
        // For other categories, sort by code with "Others" at the end
        if (codeA === 'Others' && codeB !== 'Others') {
          return 1; // Others comes after other codes
        }
        if (codeA !== 'Others' && codeB === 'Others') {
          return -1; // Other codes come before Others
        }
        // If both are "Others" or both are regular codes, sort alphabetically
        return codeA.localeCompare(codeB);
      }
      // If only a is in priority list, a comes first
      if (indexA !== -1 && indexB === -1) {
        return -1;
      }
      // If only b is in priority list, b comes first
      if (indexA === -1 && indexB !== -1) {
        return 1;
      }
      // If neither is in priority list, sort alphabetically by category first, then code
      if (categoryA !== categoryB) {
        return categoryA.localeCompare(categoryB);
      }
      // Within same category, put "Others" at the end
      if (codeA === 'Others' && codeB !== 'Others') {
        return 1;
      }
      if (codeA !== 'Others' && codeB === 'Others') {
        return -1;
      }
      return codeA.localeCompare(codeB);
    });
  };
  
  // Helper function to sort F/FS codes properly
  const sortFFSCodes = (codeA, codeB) => {
    // Priority order: F1, F2, F3, F4, F5, F6, FS1, FS2, FS3, FS4, FS5, FS6, F-Others, FS-Others
    
    const getFFSPriority = (code) => {
      if (code.startsWith('F') && !code.startsWith('FS') && !code.includes('Others')) {
        const num = parseInt(code.substring(1));
        if (!isNaN(num)) return num; // F1=1, F2=2, etc.
      }
      if (code.startsWith('FS') && !code.includes('Others')) {
        const num = parseInt(code.substring(2));
        if (!isNaN(num)) return 10 + num; // FS1=11, FS2=12, etc.
      }
      if (code === 'F-Others') return 100;
      if (code === 'FS-Others') return 101;
      return 999; // Other codes last
    };
    
    const priorityA = getFFSPriority(codeA);
    const priorityB = getFFSPriority(codeB);
    
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // If same priority, sort alphabetically
    return codeA.localeCompare(codeB);
  };
  
  const sortedCompositeKeys = sortByReasonCodeCategoryAndCode(allCompositeKeys);
  
  const processedRows = sortedCompositeKeys.map((compositeKey, index) => {
    const data = dataMap[compositeKey];
    const row = {
      id: index,
      reasonCode: data.reasonCode
    };

    monthsToShow.forEach(month => {    
      // Check if this composite key exists in dataMap and has data for this month
      let monthData = { count: 0, value: 0 };
      if (data.monthlyData[month.key]) {
        monthData = data.monthlyData[month.key];
      }
      
      const value = volumeType === 'value' ? monthData.value : monthData.count;
      // Replace 0 with "-" when there's no data
      row[month.key] = value === 0 ? "-" : value;
    });

    return row;
  });
  
  return { processedColumns, processedRows };
};

// Get grid data with applied filters (only after Apply button is clicked)
export const getFilteredData = (reportData, appliedFilters) => {
  if (!reportData || reportData.length === 0) return [];
  
  // Customer filtering is now handled in processReportData, so just return the data as-is
  return [...reportData];
};



// Prepare data for bar chart with exact individual reason codes from raw data
export const getStackedChartData = (filteredData, columns, rawData = null, appliedFilters = null, filters = null, selectedCustomerName = '') => {
  if (!rawData || rawData.length === 0) return [];

  // Generate months based on applied filters or default to last 6 months (same logic as table)
  let monthsToShow = [];
  
  // Priority: 1. Applied filters, 2. Current filter selections (if complete), 3. Default last 6 months
  const useFilters = (appliedFilters && appliedFilters.startMonth && appliedFilters.endMonth && appliedFilters.startYear && appliedFilters.endYear) 
    ? appliedFilters 
    : (filters && filters.startMonth && filters.endMonth && filters.startYear && filters.endYear) 
      ? filters 
      : null;
  
  if (useFilters) {
    // Generate months from start to end based on selected date range
    const startDate = dayjs(`${useFilters.startYear}-${String(useFilters.startMonth).padStart(2, '0')}-01`);
    const endDate = dayjs(`${useFilters.endYear}-${String(useFilters.endMonth).padStart(2, '0')}-01`);
        
    let currentDate = startDate;
    while (currentDate.valueOf() <= endDate.valueOf()) {
      monthsToShow.push({
        key: currentDate.format('YYYY-MM'),
        label: currentDate.format('MMM-YY')
      });
      currentDate = currentDate.add(1, 'month');
    }
  } else {
    // Default: show last 6 months if no date range is applied
    const currentDate = dayjs();
    for (let i = 5; i >= 0; i--) {
      const monthDate = currentDate.subtract(i, 'month');
      monthsToShow.push({
        key: monthDate.format('YYYY-MM'),
        label: monthDate.format('MMM-YY')
      });
    }
  }

  // Get month keys from the generated months (not from table columns)
  const monthKeys = monthsToShow;

  // Determine volume type - prioritize current filters over applied filters for immediate response
  const volumeType = (filters && filters.volume) || (appliedFilters && appliedFilters.volume) || 'count';

  // Apply the same filtering logic as the table but preserve exact reason codes for chart
  let chartRawData = rawData;
  
  // Filter by customer name if selected
  if (selectedCustomerName && selectedCustomerName.trim() !== '') {
    chartRawData = chartRawData.filter(item => 
      item.customerName && item.customerName === selectedCustomerName
    );
  }

  // Filter by new reason code category if applied filters exist and new reason code is selected
  // BUT keep the exact individual codes for the chart
  if (appliedFilters && appliedFilters.newReasonCode && appliedFilters.newReasonCode.trim() !== '') {
    chartRawData = chartRawData.filter(item => {
      if (!item.newReasonCode) return false;
      
      // Categorize the reason code to match the filter
      let categoryKey = '';
      if ((item.newReasonCode.startsWith('F') && !item.newReasonCode.startsWith('FU')) || item.newReasonCode.startsWith('FS')) {
        categoryKey = 'F/FS';
      } else if (item.newReasonCode.startsWith('CF') || item.newReasonCode.startsWith('CL')) {
        categoryKey = 'CF/CL';
      } else if (item.newReasonCode.startsWith('FU')) {
        categoryKey = 'FU';
      }
      
      return categoryKey === appliedFilters.newReasonCode;
    });
  }

  // Process raw data directly to get exact reason codes
  const chartDataMap = {};
  
  // Initialize chart data structure
  monthKeys.forEach(month => {
    chartDataMap[month.key] = { month: month.label };
  });

  // Process each item in filtered raw data
  chartRawData.forEach(item => {
    if (item.newReasonCode && item.processingtime) {
      const processingMonth = dayjs(item.processingtime).format('YYYY-MM');
      
      // Apply the same "Others" grouping logic as the table
      let chartReasonCode = item.newReasonCode;
      
      // Categorize the reason code first
      let reasonCodeCategory = '';
      if ((item.newReasonCode.startsWith('F') && !item.newReasonCode.startsWith('FU')) || item.newReasonCode.startsWith('FS')) {
        reasonCodeCategory = 'F/FS';
      } else if (item.newReasonCode.startsWith('CF') || item.newReasonCode.startsWith('CL')) {
        reasonCodeCategory = 'CF/CL';
      } else if (item.newReasonCode.startsWith('FU')) {
        reasonCodeCategory = 'FU';
      } else {
        reasonCodeCategory = 'Others';
      }

      // Apply the same detailed grouping logic as the table
      if (reasonCodeCategory === 'F/FS') {
        if (chartReasonCode.startsWith('F') && !chartReasonCode.startsWith('FU') && !chartReasonCode.startsWith('FS')) {
          // Handle F codes (F1, F2, F3, F4, F5, F6)
          const numPart = chartReasonCode.substring(1); // Remove 'F'
          const num = parseInt(numPart);
          if (!isNaN(num) && num > 6) {
            chartReasonCode = 'F-Others';
          }
          // Keep F1-F6 as is
        } else if (chartReasonCode.startsWith('FS')) {
          // Handle FS codes (FS1, FS2, FS3, FS4, FS5, FS6)
          const numPart = chartReasonCode.substring(2); // Remove 'FS'
          const num = parseInt(numPart);
          if (!isNaN(num) && num > 6) {
            chartReasonCode = 'FS-Others';
          }
          // Keep FS1-FS6 as is
        } else {
          // Any other F-type codes that don't match pattern
          chartReasonCode = 'F-Others';
        }
      } else {
        // For other categories, apply the same logic as table
        if (chartReasonCode) {
          let shouldCategorizeAsOthers = false;
          
          // Check CF codes (CF7, CF8, CF9, etc.)
          if (chartReasonCode.startsWith('CF')) {
            const numPart = chartReasonCode.substring(2); // Remove 'CF'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          // Check CL codes (CL7, CL8, CL9, etc.)
          else if (chartReasonCode.startsWith('CL')) {
            const numPart = chartReasonCode.substring(2); // Remove 'CL'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          // Check FU codes (FU7, FU8, FU9, etc.)
          else if (chartReasonCode.startsWith('FU')) {
            const numPart = chartReasonCode.substring(2); // Remove 'FU'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          
          if (shouldCategorizeAsOthers) {
            chartReasonCode = 'Others';
          }
        }
      }
      
      // Only process if this month is in our display range
      if (chartDataMap[processingMonth]) {
        // Initialize reason code if not exists
        if (!chartDataMap[processingMonth][chartReasonCode]) {
          chartDataMap[processingMonth][chartReasonCode] = 0;
        }
        
        // Add the count or value based on volume type
        if (volumeType === 'value') {
          const amountToExtend = parseFloat(item.amountToExtend) || 0;
          chartDataMap[processingMonth][chartReasonCode] += amountToExtend;
        } else {
          // Count mode
          chartDataMap[processingMonth][chartReasonCode] += 1;
        }
      }
    }
  });

  // Convert to array format required by chart
  return Object.values(chartDataMap);
};



// Get stack keys for chart (exact individual reason codes from filtered raw data)
export const getStackKeysForChart = (filteredData, rawData = null, appliedFilters = null, selectedCustomerName = '') => {
  if (!rawData || rawData.length === 0) return [];
  
  // Apply the same filtering logic as the chart data function
  let chartRawData = rawData;
  
  // Filter by customer name if selected
  if (selectedCustomerName && selectedCustomerName.trim() !== '') {
    chartRawData = chartRawData.filter(item => 
      item.customerName && item.customerName === selectedCustomerName
    );
  }

  // Filter by new reason code category if applied filters exist and new reason code is selected
  if (appliedFilters && appliedFilters.newReasonCode && appliedFilters.newReasonCode.trim() !== '') {
    chartRawData = chartRawData.filter(item => {
      if (!item.newReasonCode) return false;
      
      // Categorize the reason code to match the filter
      let categoryKey = '';
      if ((item.newReasonCode.startsWith('F') && !item.newReasonCode.startsWith('FU')) || item.newReasonCode.startsWith('FS')) {
        categoryKey = 'F/FS';
      } else if (item.newReasonCode.startsWith('CF') || item.newReasonCode.startsWith('CL')) {
        categoryKey = 'CF/CL';
      } else if (item.newReasonCode.startsWith('FU')) {
        categoryKey = 'FU';
      }
      
      return categoryKey === appliedFilters.newReasonCode;
    });
  }
  
  // Get unique reason codes from the filtered raw data and apply the same "Others" grouping logic
  const processedReasonCodes = new Set();
  
  chartRawData.forEach(item => {
    if (item.newReasonCode) {
      let chartReasonCode = item.newReasonCode;
      
      // Apply the same "Others" grouping logic as the chart data processing
      let reasonCodeCategory = '';
      if ((item.newReasonCode.startsWith('F') && !item.newReasonCode.startsWith('FU')) || item.newReasonCode.startsWith('FS')) {
        reasonCodeCategory = 'F/FS';
      } else if (item.newReasonCode.startsWith('CF') || item.newReasonCode.startsWith('CL')) {
        reasonCodeCategory = 'CF/CL';
      } else if (item.newReasonCode.startsWith('FU')) {
        reasonCodeCategory = 'FU';
      } else {
        reasonCodeCategory = 'Others';
      }

      // Apply the same detailed grouping logic as the chart data
      if (reasonCodeCategory === 'F/FS') {
        if (chartReasonCode.startsWith('F') && !chartReasonCode.startsWith('FU') && !chartReasonCode.startsWith('FS')) {
          // Handle F codes (F1, F2, F3, F4, F5, F6)
          const numPart = chartReasonCode.substring(1); // Remove 'F'
          const num = parseInt(numPart);
          if (!isNaN(num) && num > 6) {
            chartReasonCode = 'F-Others';
          }
          // Keep F1-F6 as is
        } else if (chartReasonCode.startsWith('FS')) {
          // Handle FS codes (FS1, FS2, FS3, FS4, FS5, FS6)
          const numPart = chartReasonCode.substring(2); // Remove 'FS'
          const num = parseInt(numPart);
          if (!isNaN(num) && num > 6) {
            chartReasonCode = 'FS-Others';
          }
          // Keep FS1-FS6 as is
        } else {
          // Any other F-type codes that don't match pattern
          chartReasonCode = 'F-Others';
        }
      } else {
        // For other categories, apply the same logic as chart data
        if (chartReasonCode) {
          let shouldCategorizeAsOthers = false;
          
          // Check CF codes (CF7, CF8, CF9, etc.)
          if (chartReasonCode.startsWith('CF')) {
            const numPart = chartReasonCode.substring(2); // Remove 'CF'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          // Check CL codes (CL7, CL8, CL9, etc.)
          else if (chartReasonCode.startsWith('CL')) {
            const numPart = chartReasonCode.substring(2); // Remove 'CL'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          // Check FU codes (FU7, FU8, FU9, etc.)
          else if (chartReasonCode.startsWith('FU')) {
            const numPart = chartReasonCode.substring(2); // Remove 'FU'
            const num = parseInt(numPart);
            if (!isNaN(num) && num > 6) {
              shouldCategorizeAsOthers = true;
            }
          }
          
          if (shouldCategorizeAsOthers) {
            chartReasonCode = 'Others';
          }
        }
      }
      
      processedReasonCodes.add(chartReasonCode);
    }
  });
  
  // Convert to array and sort reason codes alphabetically for consistent display
  return [...processedReasonCodes].sort();
};